import React, { FC, useContext, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { routes } from "@app/routing";
import { Button, Form, Textfield } from "@natera/form";
import { ConfigContext, ProfileContext, UserContext } from "@app/provider";
import { validateEmail } from "@app/utils";

import MailIcon from "@assets/svg/icons/mail.svg";
import { FormField, InfoCard, Notification } from "@app/components";
import "./updateEmail.scss";
import { BlueHeaderContent } from "@app/components/ui/layout";

const messages = defineMessages({
  updateEmailBackButton: {
    id: "updateEmailBackButton",
    defaultMessage: "Contact Details",
  },
  updateEmailTitle: {
    id: "updateEmailTitle",
    defaultMessage: "Email",
  },
  updateEmailInputLabel: {
    id: "updateEmailInputLabel",
    defaultMessage: "Email Address",
  },
  updateEmailCancelButton: {
    id: "updateEmailCancelButton",
    defaultMessage: "Cancel",
  },
  updateEmailUpdateButon: {
    id: "updateEmailUpdateButon",
    defaultMessage: "Update",
  },
  updateEmailNotification: {
    id: "updateEmailNotification",
    defaultMessage: "A verification link will be sent to this email address.",
  },
  updateEmailInvalidError: {
    id: "updateEmailInvalidError",
    defaultMessage: "Please enter a valid email address.",
  },
  updateEmailGenericError: {
    id: "updateEmailGenericError",
    defaultMessage: "Something went wrong. Please try again.",
  },
});

const UpdateEmail: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { config } = React.useContext(ConfigContext);
  const { getProfileData: profile } = useContext(ProfileContext);
  const { updateEmail } = useContext(UserContext);
  const [email, setEmail] = useState("");
  const [emailInputError, setEmailInputError] = useState("");
  const [hasServerError, setHasServerError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (profile?.email) {
      setEmail(profile.email);
    }
  }, [profile?.email]);

  const usePlusSignInEmailAddress =
    config.test.usePlusSignInEmailAddress.enabled;

  const normalize = (value: string) => value.trim().toLowerCase();
  const isEmailUnchanged =
    !!profile?.email && normalize(email) === normalize(profile.email);

  const update = (newEmail: string) => {
    setHasServerError(false);
    setIsSubmitting(true);
    updateEmail(newEmail)
      .then((resp) => {
        if (resp?.newEmail) {
          navigate(routes.resendUpdateEmail, { replace: true });
        }
      })
      .catch(() => {
        setHasServerError(true);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const submit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (isSubmitting || isEmailUnchanged) {
      return;
    }

    if (!validateEmail(email, usePlusSignInEmailAddress)) {
      setEmailInputError(intl.formatMessage(messages.updateEmailInvalidError));
      return;
    }

    update(email);
  };

  const goToContactDetails = () => {
    navigate(routes.contactDetails, { replace: true });
  };

  if (!profile) {
    return null;
  }

  return (
    <BlueHeaderContent
      blueHeaderTitle={intl.formatMessage(messages.updateEmailBackButton)}
      backArrowHeaderRoute={routes.contactDetails}
    >
      <InfoCard titleText={intl.formatMessage(messages.updateEmailTitle)}>
        <Form className="update-email-form" onSubmit={submit}>
          {hasServerError && (
            <Notification
              className="update-email-notification"
              type="error"
              role="alert"
            >
              {intl.formatMessage(messages.updateEmailGenericError)}
            </Notification>
          )}
          <FormField
            label={intl.formatMessage(messages.updateEmailInputLabel)}
            error={emailInputError}
            required
            htmlFor="email"
            withPadding
          >
            <Textfield
              value={email}
              outline
              required
              id="email"
              type="email"
              name="email"
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailInputError("");
              }}
              onInvalid={() =>
                setEmailInputError(
                  intl.formatMessage(messages.updateEmailInvalidError)
                )
              }
              autoComplete="email"
            />
          </FormField>
          <Notification
            className="update-email-notification"
            icon={MailIcon}
            type="note"
          >
            {intl.formatMessage(messages.updateEmailNotification)}
          </Notification>

          <div className="buttons-container">
            <Button
              type="button"
              onClick={goToContactDetails}
              outlined
              disabled={isSubmitting}
            >
              {intl.formatMessage(messages.updateEmailCancelButton)}
            </Button>

            <Button
              type="submit"
              unelevated
              loading={isSubmitting}
              disabled={isEmailUnchanged}
            >
              {!isSubmitting &&
                intl.formatMessage(messages.updateEmailUpdateButon)}
            </Button>
          </div>
        </Form>
      </InfoCard>
    </BlueHeaderContent>
  );
};

export default UpdateEmail;
